// src/context/AuthContext.js
import React, { createContext, useContext, useEffect, useState } from "react";
import { auth } from "../firebase";
import { onAuthStateChanged, signOut, signInWithPopup, GoogleAuthProvider } from "firebase/auth";
import { doc, getDoc, getFirestore, setDoc, deleteDoc } from "firebase/firestore";
import data from "../data/content.json"; // Assuming this is your content file

const AuthContext = createContext();
const db = getFirestore();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // Firebase user
  const [userData, setUserData] = useState(null); // Extra metadata from Firestore
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState(null);

  const userExistsInFirestore = async (uid) => {
    const userRef = doc(db, "users", uid);
    const userSnap = await getDoc(userRef);
    return userSnap.exists();
  };

  const checkUserExists = async (uid) => {
    if (!userExistsInFirestore(uid)) {
      const user = auth.currentUser;
      if (user) {
        await saveUserMetadata(user);
      }
    }
  };

  const saveUserMetadata = async (user) => {
    if (user) {
      const generateRandomCode = () => Math.floor(100000 + Math.random() * 900000).toString();

      await setDoc(doc(db, "users", user.uid), {
        userId: user.uid,
        email: user.email,
        firstName: user.displayName ? user.displayName.split(" ")[0] : null,
        lastName: user.displayName ? user.displayName.split(" ")[1] : null,
        displayName: user.displayName,
        photoURL: user.photoURL,
        phoneNumber: user.phoneNumber,
        activationCode: generateRandomCode(),
        activatedAt: null,
        language: "en",
        accountType: "free", // free, pro, premium, unlimited
        imType: "whatsapp", // whatsapp, telegram
        imState: "inactive", // active, inactive
        imId: null,
        createdAt: new Date(),
      });
    }
  };

  const deleteUser = async () => {
    const user = auth.currentUser;
    if (user) {
      await deleteDoc(doc(db, "users", user.uid));
      await signOut(auth);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);

        // 🔥 Fetch extra metadata from Firestore
        const docRef = doc(db, "users", firebaseUser.uid);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          setUserData(docSnap.data());
        } else {
          setUserData(null); // or handle fallback
        }
      } else {
        setUser(null);
        setUserData(null);
      }

      setLoading(false);
    });

    const fetchLandingPageContent = async () => {
      const isDevelopment = process.env.NODE_ENV === 'development';
      if (isDevelopment) {
        setContent(data);
        return;
      }

      const docRef = doc(db, "content", "content_landing_page_plant_finder_english");
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        setContent(JSON.parse(docSnap.data().value));
      } else {
        setContent(data); // or handle fallback
      }
    };
    fetchLandingPageContent();

    return () => unsubscribe();
  }, [content]);

  const logout = () => signOut(auth);
  const loginWithGoogle = () => signInWithPopup(auth, new GoogleAuthProvider());

  return (
    <AuthContext.Provider
      value={{
        user,
        userData,
        isLoggedIn: !!user,
        loading,
        logout,
        loginWithGoogle,
        checkUserExists,
        deleteUser,
        content,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within an AuthProvider");
  return context;
};